/**
 * Shared scaffolding for the level generators.
 *
 * A generator profile is handed a GenSite, carves into `site.grid` and appends
 * labelled areas to `site.regions`. Everything after that — connectivity
 * repair, placement, assembling the GeneratedLevel — is done for it by
 * `generator.ts`, so the profiles stay small and are free to carve optimistically.
 */

import { makeGrid } from '../../core/grid';
import { Cell, type Grid, type LevelSpec, type Region, type RegionKind, type RNG } from '../../core/types';

/** A block of cells covering x..x+w-1, y..y+h-1. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The mutable world a generator profile builds into. */
export interface GenSite {
  readonly spec: LevelSpec;
  readonly grid: Grid;
  readonly rng: RNG;
  /** Labelled areas in carve order. `Region.id` is the index into this array. */
  readonly regions: Region[];
}

export function makeSite(spec: LevelSpec, rng: RNG): GenSite {
  return {
    spec,
    grid: makeGrid(spec.gridW, spec.gridH, spec.cellSize, Cell.Wall),
    rng,
    regions: [],
  };
}

/**
 * Everything inside the one-cell outer wall. Generators carve here and nowhere
 * else, which guarantees the level is sealed and lets connectivity repair route
 * through any interior cell without leaking outside the map.
 */
export function interiorRect(grid: Grid): Rect {
  return { x: 1, y: 1, w: grid.w - 2, h: grid.h - 2 };
}

/** Shrink (or, with a negative n, grow) a rect on every side. */
export function inset(r: Rect, n: number): Rect {
  return { x: r.x + n, y: r.y + n, w: r.w - 2 * n, h: r.h - 2 * n };
}

/** True when the rects touch or overlap once both are grown by `pad`. */
export function rectsOverlap(a: Rect, b: Rect, pad = 0): boolean {
  return (
    a.x - pad < b.x + b.w && b.x - pad < a.x + a.w && a.y - pad < b.y + b.h && b.y - pad < a.y + a.h
  );
}

export function fillRect(grid: Grid, r: Rect, cell: Cell): void {
  const x1 = Math.min(grid.w, r.x + r.w);
  const y1 = Math.min(grid.h, r.y + r.h);
  for (let y = Math.max(0, r.y); y < y1; y++) {
    for (let x = Math.max(0, r.x); x < x1; x++) grid.set(x, y, cell);
  }
}

/** The one-cell ring around the outside edge of `r`. */
export function strokeRect(grid: Grid, r: Rect, cell: Cell): void {
  for (let x = r.x; x < r.x + r.w; x++) {
    grid.set(x, r.y, cell);
    grid.set(x, r.y + r.h - 1, cell);
  }
  for (let y = r.y; y < r.y + r.h; y++) {
    grid.set(r.x, y, cell);
    grid.set(r.x + r.w - 1, y, cell);
  }
}

/** Axis-aligned run of cells, inclusive of both ends. */
export function carveLine(grid: Grid, x0: number, y0: number, x1: number, y1: number, cell: Cell): void {
  if (x0 === x1) {
    const a = Math.min(y0, y1);
    const b = Math.max(y0, y1);
    for (let y = a; y <= b; y++) grid.set(x0, y, cell);
  } else if (y0 === y1) {
    const a = Math.min(x0, x1);
    const b = Math.max(x0, x1);
    for (let x = a; x <= b; x++) grid.set(x, y0, cell);
  }
}

/** Square lattice of support pillars, kept clear of the region's walls. */
export function pillarLattice(grid: Grid, r: Rect, step: number, offset = 2): void {
  for (let y = r.y + offset; y < r.y + r.h - 1; y += step) {
    for (let x = r.x + offset; x < r.x + r.w - 1; x += step) {
      if (grid.at(x, y) === Cell.Floor) grid.set(x, y, Cell.Pillar);
    }
  }
}

export function addRegion(site: GenSite, r: Rect, kind: RegionKind): Region {
  const region: Region = {
    id: site.regions.length,
    x: r.x,
    y: r.y,
    w: r.w,
    h: r.h,
    kind,
    center: site.grid.cellToWorld(r.x + (r.w >> 1), r.y + (r.h >> 1)),
  };
  site.regions.push(region);
  return region;
}

/** How many of the four orthogonal neighbours can be stood in. */
export function countWalkableNeighbors(grid: Grid, cx: number, cy: number): number {
  let n = 0;
  if (grid.isWalkable(cx + 1, cy)) n++;
  if (grid.isWalkable(cx - 1, cy)) n++;
  if (grid.isWalkable(cx, cy + 1)) n++;
  if (grid.isWalkable(cx, cy - 1)) n++;
  return n;
}

/** True when the cell has at least one solid orthogonal neighbour. */
export function touchesSolid(grid: Grid, cx: number, cy: number): boolean {
  return grid.isSolid(cx + 1, cy) || grid.isSolid(cx - 1, cy) || grid.isSolid(cx, cy + 1) || grid.isSolid(cx, cy - 1);
}

/**
 * Demote walls that nothing can ever see to Void. The generators fill the map
 * solid and carve into it, which leaves large blocks of Wall buried inside the
 * rock; the renderer only ever needs the one-cell shell around walkable space.
 */
export function shellify(grid: Grid): void {
  const { w, h, cells } = grid;
  const out = new Uint8Array(cells.length);
  out.set(cells);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (cells[i] !== Cell.Wall) continue;
      let seen = false;
      for (let dy = -1; dy <= 1 && !seen; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = x + dx;
          const ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
          const c = cells[ny * w + nx]!;
          if (c !== Cell.Void && c !== Cell.Wall) {
            seen = true;
            break;
          }
        }
      }
      if (!seen) out[i] = Cell.Void;
    }
  }
  cells.set(out);
}

/* ------------------------------------------------------------------ *
 * Connectivity
 * ------------------------------------------------------------------ */

const DX = [1, -1, 0, 0];
const DY = [0, 0, 1, -1];

/**
 * Buffers and running total for `guardedEdit`. Made once per pass so that a
 * guard costs one flood fill rather than one flood fill and three allocations.
 */
export interface ConnectivityGuard {
  readonly grid: Grid;
  /** Walkable cells outside the largest component, as of the last accepted edit. */
  stranded: number;
  readonly seen: Uint8Array;
  readonly queue: Int32Array;
  readonly snapshot: Uint8Array;
}

/**
 * Walkable cells outside the largest walkable component.
 *
 * The number a carving pass has to keep from growing. A profile is allowed to
 * hand `repairConnectivity` a grid with pockets in it and let it tunnel through
 * — but a tunnel through solid rock is a scar, not a plan, so any pass that
 * could genuinely seal something off measures this before and after itself and
 * rolls back rather than leaving the repair to guess.
 */
export function strandedCells(grid: Grid, scratch?: ConnectivityGuard): number {
  const cells = grid.cells;
  const n = cells.length;
  const seen = scratch ? scratch.seen : new Uint8Array(n);
  const queue = scratch ? scratch.queue : new Int32Array(n);
  if (scratch) seen.fill(0);
  let total = 0;
  let largest = 0;
  for (let seed = 0; seed < n; seed++) {
    if (seen[seed]) continue;
    const sc = cells[seed]!;
    if (sc === Cell.Void || sc === Cell.Wall) continue;
    let head = 0;
    let tail = 0;
    seen[seed] = 1;
    queue[tail++] = seed;
    let size = 0;
    while (head < tail) {
      const idx = queue[head++]!;
      size++;
      const cx = idx % grid.w;
      const cy = (idx / grid.w) | 0;
      for (let d = 0; d < 4; d++) {
        const nx = cx + DX[d]!;
        const ny = cy + DY[d]!;
        if (nx < 0 || ny < 0 || nx >= grid.w || ny >= grid.h) continue;
        const j = ny * grid.w + nx;
        if (seen[j]) continue;
        const v = cells[j]!;
        if (v === Cell.Void || v === Cell.Wall) continue;
        seen[j] = 1;
        queue[tail++] = j;
      }
    }
    total += size;
    if (size > largest) largest = size;
  }
  return total - largest;
}

export function makeGuard(grid: Grid): ConnectivityGuard {
  const n = grid.cells.length;
  const guard: ConnectivityGuard = {
    grid,
    stranded: 0,
    seen: new Uint8Array(n),
    queue: new Int32Array(n),
    snapshot: new Uint8Array(n),
  };
  guard.stranded = strandedCells(grid, guard);
  return guard;
}

/**
 * Apply `edit`, keep it if it stranded nothing new, roll it back if it did.
 *
 * "Stranded nothing new" rather than "left the level connected" because the
 * grid may already have pockets in it; what a guarded pass owns is that it did
 * not make that worse. Rolling back the whole grid rather than the edited cells
 * is a memcpy of twenty kilobytes and means an edit can be written with no
 * bookkeeping of its own.
 */
export function guardedEdit(guard: ConnectivityGuard, edit: () => void): boolean {
  guard.snapshot.set(guard.grid.cells);
  edit();
  const after = strandedCells(guard.grid, guard);
  if (after <= guard.stranded) {
    guard.stranded = after;
    return true;
  }
  guard.grid.cells.set(guard.snapshot);
  return false;
}

/** Flat indices of every walkable cell, in row-major order. */
export function collectWalkable(grid: Grid): Int32Array {
  const { w, h } = grid;
  let count = 0;
  for (let i = 0; i < grid.cells.length; i++) {
    const c = grid.cells[i]!;
    if (c !== Cell.Void && c !== Cell.Wall) count++;
  }
  const out = new Int32Array(count);
  let k = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const c = grid.cells[i]!;
      if (c !== Cell.Void && c !== Cell.Wall) out[k++] = i;
    }
  }
  return out;
}

/**
 * Region id per cell, later regions overwriting earlier ones. Overlays such as
 * corridors are appended last precisely so they win this lookup.
 */
export function buildRegionIndex(site: GenSite): Int32Array {
  const { grid, regions } = site;
  const idx = new Int32Array(grid.w * grid.h).fill(-1);
  for (const r of regions) {
    const x1 = Math.min(grid.w, r.x + r.w);
    const y1 = Math.min(grid.h, r.y + r.h);
    for (let y = Math.max(0, r.y); y < y1; y++) {
      const row = y * grid.w;
      for (let x = Math.max(0, r.x); x < x1; x++) idx[row + x] = r.id;
    }
  }
  return idx;
}

/**
 * Pull every region centre onto a cell something can stand in. Systems use
 * `Region.center` as a navigation target, and a warren's geometric middle is
 * usually a wall.
 */
export function snapRegionCenters(site: GenSite): void {
  const { grid } = site;
  for (const r of site.regions) {
    const cx = r.x + (r.w >> 1);
    const cy = r.y + (r.h >> 1);
    if (grid.isWalkable(cx, cy)) continue;
    let best = -1;
    let bestD = Infinity;
    const x1 = r.x + r.w;
    const y1 = r.y + r.h;
    for (let y = r.y; y < y1; y++) {
      for (let x = r.x; x < x1; x++) {
        if (!grid.isWalkable(x, y)) continue;
        const d = (x - cx) * (x - cx) + (y - cy) * (y - cy);
        if (d < bestD) {
          bestD = d;
          best = y * grid.w + x;
        }
      }
    }
    if (best >= 0) r.center = grid.cellToWorld(best % grid.w, (best / grid.w) | 0);
  }
}
